import { useCallback, useMemo } from 'react';

import { AuthShareQuery } from 'dashboard-services/api/auth';
import { DataLakeEnums, DataLakeQuery } from 'dashboard-services/api/dataLake';
import { CommonService, ReduxService } from 'dashboard-services/services';

import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

export default(() => {
  const getGroupsQuery = ({ fullQuery, groupName, query }) => fullQuery ? fullQuery : (groupName ? `name.keyword=${groupName}` : (query ? `name=*${String(query).toLowerCase()}*` : undefined))

  const useSearchGroups = ({ query, provider, groupName, tags, shouldBeLimited, getQueryBuilder, size = CommonService.getRequestSize(), shouldRefetch = true, enabled = true, onSelect, fullQuery, ...rest }) =>
    useInfiniteQuery({
      ...DataLakeQuery.QUERIES.searchGroups(
        getQueryBuilder,
        {
          query: getGroupsQuery({ fullQuery, groupName, query }),
          size,
          sortBy: DataLakeEnums.GROUPS_SORT_BY.NAME,
          sortOrder: "asc",
          provider,
          groupTags: tags ? tags?.split(",") : undefined,
        }
      ),
      initialPageParam: 0,
      refetchInterval: () => !shouldRefetch || shouldBeLimited(provider) ? undefined : 10000,
      getNextPageParam: CommonService.getNextPageParam,
      enabled,
      select: onSelect,
      ...rest
    })

  const useSearchGroupsMutation = ({ query, provider, tags, getQueryBuilder, size = CommonService.getRequestSize(), ...rest }) =>
    useMutation({
      ...DataLakeQuery.QUERIES.searchGroupsMutation(
        getQueryBuilder,
        {
          query,
          size,
          sortBy: DataLakeEnums.GROUPS_SORT_BY.NAME,
          sortOrder: "asc",
          provider,
          groupTags: tags ? tags?.split(",") : undefined,
        }
      ),
      ...rest
    })
  
  const useFavoriteGroups = ({ favoriteGroups, getQueryBuilder, shouldBeLimited, userName }) => {
    const queries = useMemo(() => favoriteGroups.map(group => ({
            ...DataLakeQuery.QUERIES.getGroup(
              getQueryBuilder,
              {
                groupName: group.name,
                favorite: true
              },
            ),
            enabled: !!userName && !!group.name,
            refetchInterval: () => shouldBeLimited() ? undefined : 10000,
            refetchOnWindowFocus: false
          })), [favoriteGroups, getQueryBuilder, shouldBeLimited, userName]),
          combine = useCallback(results => ({
            data: results.map(result => result.data).filter(g => g?.name !== undefined).map(g => ReduxService.updateObject(g, { favorite: true })),
            isPending: results.some(result => result.isPending),
          }), [])
    return useQueries({
      queries,
      combine
    })
  }

  const useCreateGroup = ({ description, fields, getQueryBuilder, groupTags, name }) => {
    const queryClient = useQueryClient()
    return useMutation({
      ...DataLakeQuery.QUERIES.createGroup(
        getQueryBuilder,
        {
          name: name ? String(name)?.trim() : name,
          description,
          fields,
          groupTags
        }
      ),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_GROUPS() }, { queryKey: DataLakeQuery.KEYS.GET_GROUP() }),
    })
  }

  const useSearchGroupsTags = ({ getQueryBuilder, input, shouldBeLimited, provider }) =>
    useInfiniteQuery({
      ...DataLakeQuery.QUERIES.getGroupsTags(
        getQueryBuilder,
        { 
          query: input ? `*${input}*` : "*",
          size: 50,
          keys: { input, provider }
        }
      ),
      initialPageParam: 0,
      staleTime: 10_000, // 10 seconds
      refetchInterval: () => shouldBeLimited(provider) ? undefined : 15000,
      getNextPageParam: CommonService.getNextPageParam
    })

  const useGetGroup = ({ groupName, provider, getQueryBuilder, shouldBeLimited }) =>
    useQuery({
      ...DataLakeQuery.QUERIES.getGroup(
        getQueryBuilder,
        {
          groupName,
          provider
        }
      ),
      refetchInterval: () => shouldBeLimited(provider) ? undefined : 10000,
      enabled: !!groupName
    })

  const useGetGroupMutation = ({ getQueryBuilder, groupName }) =>
    useMutation({
      ...DataLakeQuery.QUERIES.getGroupMutation(
        getQueryBuilder,
        {
          groupName
        }
      ),
    })

  const useEditGroup = ({ description, fields, getQueryBuilder, groupName, groupTags }) => {
    const queryClient = useQueryClient()
    return useMutation({
      ...DataLakeQuery.QUERIES.updateGroup(
        getQueryBuilder,
        {
          groupName,
          description,
          fields,
          groupTags
        }
      ),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_GROUPS() }, { queryKey: DataLakeQuery.KEYS.GET_GROUP() })
    })
  }

  const useRemoveGroup = ({ groupName, getQueryBuilder }) => {
    const queryClient = useQueryClient()
    return useMutation({
      ...DataLakeQuery.QUERIES.deleteGroup(
        getQueryBuilder,
        {
          groupName
        }
      ),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_GROUPS() }),
    })
  }

  const useGetGroupSharedWith = ({ getQueryBuilder, groupName, enabled = true }) =>
    useQuery({
      ...AuthShareQuery.QUERIES.getListOfUsers(
        getQueryBuilder,
        {
          group: groupName
        }
      ),
      enabled: enabled && !!groupName,
      refetchOnMount: "always"
    })

  const useShareGroup = ({ getQueryBuilder }) =>
    useMutation({
      ...AuthShareQuery.QUERIES.share(
        getQueryBuilder
      )
    })

  const useUnShareGroup = ({ getQueryBuilder }) =>
    useMutation({
      ...AuthShareQuery.QUERIES.unshare(
        getQueryBuilder
      )
    })

  return {
    useSearchGroups,
    useSearchGroupsMutation,
    getGroupsQuery,
    useFavoriteGroups,
    useRemoveGroup,
    useGetGroup,
    useGetGroupMutation,
    useCreateGroup,
    useEditGroup,
    useSearchGroupsTags,
    useGetGroupSharedWith,
    useShareGroup,
    useUnShareGroup
  }
})()
